import React from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import Select from 'react-select';

import PluginFilterType from '../types/plugin_filter_type';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { capitalize, concat, includes, map, startCase, toLower, without } from 'lodash';

export default class FilterEditModal extends React.Component {
  static propTypes = {
    fieldConfig: PropTypes.object.isRequired,
    filterFieldOptions: PropTypes.object.isRequired,
    filterModel: PluginFilterType,
    propertyTypes: PropTypes.object.isRequired,
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired
  };

  componentDidUpdate() {
    const defaultOptions = this.props.fieldConfig.default;
    const selectedDefault = this.props.filterModel.defaultValue;

    if (defaultOptions && !defaultOptions[selectedDefault]) {
      this.props.onUpdate('defaultValue', Object.keys(defaultOptions)[0]);
    }
  }

  handleUpdate = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onUpdate(target.name, value);
  }

  handlePropertyTypeUpdate = (newValues) => {
    this.handleUpdate({
      target: {
        name: 'propertyTypes',
        value: newValues.map(option => option.value)
      }
    });
  }

  propertyTypeValues() {
    return this.props.filterModel.propertyTypes.map((value) => {
      return { value: value, label: this.props.propertyTypes[value] };
    });
  }

  propertyTypeOptions() {
    return map(this.props.propertyTypes, (v, k) => {
      return { value: k, label: v };
    });
  }

  propertyTypeLabel() {
    if (this.props.filterModel.propertyTypes.length === 0) {
      return 'This filter will display for any property type.';
    } else if (this.props.filterModel.propertyTypes.length === 1) {
      return (
        <span>
          This filter will display when the 'Property Type' filter is present and
          '{this.props.propertyTypes[this.props.filterModel.propertyTypes[0]]}' is selected.
        </span>
      );
    } else {
      const types = this.props.filterModel.propertyTypes.map(value => this.props.propertyTypes[value]).join(' / ');
      return (
        <span>
          This filter will display when the 'Property Type' filter is present and any of '{types}' are selected.
        </span>
      );
    }
  }

  singleSelectLabel(key, label) {
    const upperLabel = startCase(toLower(label));

    if (!this.props.filterModel[key]) {
      return `This filter will display for any ${upperLabel}.`;
    } else {
      return (
        <span>
          This filter will display when the '{upperLabel}' filter is present and
          'For {capitalize(this.props.filterModel[key])}' is selected.
        </span>
      );
    }
  }

  isAddtionalOptionPresetSelected(filterField) {
    return includes(this.props.filterModel.additionalOptionsPreset, filterField);
  }

  isPropertyTypeUseFilterSelected(filterField) {
    return includes(this.props.filterModel.propertyTypeUseFilters, filterField);
  }

  showStatusCondition() {
    return this.props.filterModel.filterField != 'status' && !this.props.fieldConfig.status;
  }

  showPropertyTypeCondition() {
    return this.props.filterModel.filterField != 'property_type';
  }

  handleAddtionalOptionPresetUpdate = (checked, filterField) => {
    this.updateListField('additionalOptionsPreset', checked, filterField);
  }

  handleAPropertyTypeUseFilterUpdate = (checked, filterField) => {
    this.updateListField('propertyTypeUseFilters', checked, filterField);
  }

  updateListField = (fieldName, checked, filterField) => {
    const existingPresets = this.props.filterModel[fieldName];

    if (checked) {
      this.props.onUpdate(fieldName, concat(existingPresets, filterField));
    } else {
      this.props.onUpdate(fieldName, without(existingPresets, filterField));
    }
  }

  allowAllSelectedLabel = () => {
    const filterField = this.props.filterFieldOptions[this.props.filterModel.filterField];

    return (filterField || {}).allowAllSelectedLabel;
  }

  allSelectedLabelInfoIcon = () => {
    return (
      <OverlayTrigger
        overlay={<Tooltip id="all-selected-label-info">
          This will be the value shown when all options are selected
        </Tooltip>}
        placement="right"
      >
        <FontAwesomeIcon className="ml-2" icon={faInfoCircle} />
      </OverlayTrigger>
    );
  }

  render() {
    return (
      <Modal
        className='filter-edit-modal'
        show={this.props.show}
        onHide={this.props.onClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Search Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h3 className='mt-0 mb-2'>Select Filter</h3>
          <p>Select which filter you want to add to your search bar</p>
          <FormGroup>
            <FormControl
              componentClass='select'
              name='filterField'
              value={this.props.filterModel.filterField}
              onChange={this.handleUpdate}
            >
              {Object.keys(this.props.filterFieldOptions).map((key) => {
                return <option key={key} value={key}>{this.props.filterFieldOptions[key].label}</option>;
              })}
            </FormControl>
            {this.props.fieldConfig.helpText &&
              <p className='help-block' name='help-block'>{ this.props.fieldConfig.helpText }</p>
            }
          </FormGroup>
          {this.props.fieldConfig.default &&
            <FormGroup>
              <ControlLabel>Default Value</ControlLabel>
              <FormControl
                componentClass='select'
                name='defaultValue'
                value={this.props.filterModel.defaultValue}
                onChange={this.handleUpdate}
              >
                {Object.keys(this.props.fieldConfig.default).map((k) => {
                  return <option key={k} value={k}>{ this.props.fieldConfig.default[k] }</option>;
                })}
              </FormControl>
            </FormGroup>
          }
          {this.props.fieldConfig.allowMetric &&
            <Checkbox checked={this.props.filterModel.metric} name='metric' onChange={this.handleUpdate}>
              Use Metric Units?
            </Checkbox>
          }
          {this.props.fieldConfig.additionalOptionsPresets &&
            map(this.props.fieldConfig.additionalOptionsPresets, (label, key) => {
              return (
                <Checkbox
                  checked={this.isAddtionalOptionPresetSelected(key)}
                  key={key}
                  name="additionalOptionsPreset"
                  onChange={e => this.handleAddtionalOptionPresetUpdate(e.target.checked, key)}
                >
                  {label}
                </Checkbox>
              );
            })
          }
          {this.props.fieldConfig.propertyTypeUseFilters &&
            <p>
              <strong>Optional:</strong>&nbsp;
              Include the following use types in the property type filter.  When using these options, do not also
              add the "Property Use" filter.
            </p>
          }
          {this.props.fieldConfig.propertyTypeUseFilters &&
            map(this.props.fieldConfig.propertyTypeUseFilters, (label, key) => {
              return (
                <Checkbox
                  checked={this.isPropertyTypeUseFilterSelected(key)}
                  key={key}
                  name="propertyTypeUseFilter"
                  onChange={e => this.handleAPropertyTypeUseFilterUpdate(e.target.checked, key)}
                >
                  {label}
                </Checkbox>
              );
            })
          }

          {this.allowAllSelectedLabel() && <FormGroup>
            <ControlLabel>
              All selected Label
              {this.allSelectedLabelInfoIcon()}
            </ControlLabel>
            <FormControl
              name="allSelectedLabel"
              value={this.props.filterModel.allSelectedLabel || ''}
              onChange={this.handleUpdate}
            />
          </FormGroup>}

          <hr />

          <h3 className='mt-0 mb-2'>Display Rules</h3>
          <p>Specify when you want this filter to display</p>

          {this.showStatusCondition() &&
            <Row className='row-eq-height'>
              <Col xs={6}>
                <FormGroup>
                  <ControlLabel>Property Status</ControlLabel>
                  <FormControl
                    componentClass='select'
                    name='saleOrLease'
                    value={this.props.filterModel.saleOrLease || ""}
                    onChange={this.handleUpdate}
                  >
                    <option value=''>Any Status</option>
                    <option value='sale'>Sale</option>
                    <option value='lease'>Lease</option>
                  </FormControl>
                </FormGroup>
              </Col>
              <Col xs={6}>
                { this.singleSelectLabel('saleOrLease', 'status') }
              </Col>
            </Row>
          }

          {this.showPropertyTypeCondition() &&
            <Row className='row-eq-height'>
              <Col xs={6}>
                <FormGroup>
                  <ControlLabel>Property Type</ControlLabel>
                  <Select
                    isMulti
                    isSearchable={false}
                    name='propertyTypes'
                    options={this.propertyTypeOptions()}
                    value={this.propertyTypeValues()}
                    onChange={this.handlePropertyTypeUpdate}
                  />
                </FormGroup>
              </Col>
              <Col xs={6}>
                { this.propertyTypeLabel() }
              </Col>
            </Row>
          }
        </Modal.Body>
        <Modal.Footer>
          <div className='text-right'>
            <div className='btn btn-sm btn-save' onClick={this.props.onClose}>Update</div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}
